import { Schema } from "effect";

/**
 * Blocker checklist codes. A blocker position, while failed, prevents a device
 * from being considered ready to pair or serve patients.
 *
 * @since 0.0.0
 */
export const CHECKLIST_BLOCKER_CODES = [
  "missing_gateway_session",
  "missing_gateway_enrollment",
  "gateway_not_ready",
  "device_revoked",
  "session_expired",
  "missing_insuite_url",
  "missing_server_token",
  "token_verification_failed",
  "general_api_unavailable",
  "missing_practice_name",
  "missing_device_name",
] as const;

/**
 * Warning checklist codes. A warning position never blocks readiness — a
 * Telematik outage or an unavailable card reader must not stop pairing or
 * check-in (see `decouple-frontdesk-verification-from-ti-status`).
 *
 * @since 0.0.0
 */
export const CHECKLIST_WARNING_CODES = [
  "telematik_unavailable",
  "selected_card_reader_unavailable",
  "medical_card_unavailable",
] as const;

/**
 * Every checklist code, blockers first then warnings.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * CHECKLIST_CODES.includes("device_revoked") // true
 * ```
 */
export const CHECKLIST_CODES = [
  ...CHECKLIST_BLOCKER_CODES,
  ...CHECKLIST_WARNING_CODES,
] as const;

const ChecklistCodeSchema = Schema.Literals([...CHECKLIST_CODES]);

/**
 * A single checklist code (blocker or warning).
 *
 * @since 0.0.0
 */
export type ChecklistCode = Schema.Schema.Type<typeof ChecklistCodeSchema>;

const ChecklistSeveritySchema = Schema.Literals(["blocker", "warning"]);

/**
 * Severity of a checklist position.
 *
 * @since 0.0.0
 */
export type ChecklistSeverity = Schema.Schema.Type<
  typeof ChecklistSeveritySchema
>;

const ChecklistStateSchema = Schema.Literals(["pending", "ok", "failed"]);

/**
 * Runtime state of a checklist position.
 *
 * @since 0.0.0
 */
export type ChecklistState = Schema.Schema.Type<typeof ChecklistStateSchema>;

const BLOCKER_CODE_SET: ReadonlySet<ChecklistCode> = new Set(
  CHECKLIST_BLOCKER_CODES,
);

/**
 * Returns the fixed severity for a checklist code.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * severityOf("telematik_unavailable") // "warning"
 * severityOf("device_revoked") // "blocker"
 * ```
 */
export function severityOf(code: ChecklistCode): ChecklistSeverity {
  return BLOCKER_CODE_SET.has(code) ? "blocker" : "warning";
}

/**
 * Deterministic, human-readable diagnosis token for a checklist code. Stable
 * per code and safe to read aloud to support (e.g. `FD-6F1A-0B.3C`-style),
 * derived without `Math.random` so it survives replay and offline contexts.
 *
 * A runtime layer backed by the diagnostic logger may override this with a
 * per-incident id where one exists; absent that, this stable token is used.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * diagnosisIdFor("device_revoked") // "FD-XXXX-XXXX" (stable per code)
 * ```
 */
export function diagnosisIdFor(code: ChecklistCode): string {
  // FNV-1a over the code string → two stable 16-bit segments.
  let hash = 0x811c9dc5;
  for (let index = 0; index < code.length; index += 1) {
    hash ^= code.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  const unsigned = hash >>> 0;
  const high = ((unsigned >>> 16) & 0xffff).toString(16).toUpperCase().padStart(4, "0");
  const low = (unsigned & 0xffff).toString(16).toUpperCase().padStart(4, "0");
  return `FD-${high}-${low}`;
}

/**
 * A single preflight checklist position — the shared, machine-readable unit
 * consumed identically by the device wizard, the Bridge pairing mirror, and
 * the device-detail drawer.
 *
 * `label` and `fix` are i18n keys, not literal copy, so every surface renders
 * the same position in the viewer's language.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const decoded = Schema.decodeUnknownSync(ChecklistPosition)({
 *   code: "missing_server_token",
 *   severity: "blocker",
 *   state: "failed",
 *   label: "onboarding.checklist.missing_server_token.label",
 *   fix: "onboarding.checklist.missing_server_token.fix",
 *   diagnosisId: "FD-1A2B-3C4D",
 * })
 * ```
 */
export class ChecklistPosition extends Schema.Class<ChecklistPosition>(
  "ChecklistPosition",
)({
  code: ChecklistCodeSchema,
  severity: ChecklistSeveritySchema,
  state: ChecklistStateSchema,
  label: Schema.String,
  fix: Schema.String,
  diagnosisId: Schema.optional(Schema.String),
}) {}

/**
 * Effect Schema for a list of checklist positions — the RPC/transport shape.
 *
 * @since 0.0.0
 */
export const ChecklistPositions = Schema.Array(ChecklistPosition);

/**
 * i18n key for a code's short label.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * labelKeyFor("device_revoked") // "onboarding.checklist.device_revoked.label"
 * ```
 */
export function labelKeyFor(code: ChecklistCode): string {
  return `onboarding.checklist.${code}.label`;
}

/**
 * i18n key for a code's single suggested fix.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * fixKeyFor("device_revoked") // "onboarding.checklist.device_revoked.fix"
 * ```
 */
export function fixKeyFor(code: ChecklistCode): string {
  return `onboarding.checklist.${code}.fix`;
}
